import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router';
import { Globe, ExternalLink, Plus } from 'lucide-react';
import {
  Card,
  Button,
  StatusBadge,
  type StatusTone,
  NotificationCard,
  UniversalEmptyState,
  UniversalLoadingState,
  useToast,
} from '@sighthost/ui';
import { supabase } from '@/shared/lib/supabase';
import type { CustomerDetailContext } from './customerDetailTypes';
import { RegisterDomainModal } from './RegisterDomainModal';
import { formatDate } from '@/shared/utils/format';

/**
 * CustomerDomainsTab — lists this customer's domain_link rows and lets admin
 * register a new domain on their behalf.
 *
 * Schema (verified 2026-06-02 via information_schema):
 *   domain_link has: id, customer_id, domain_name, registrar, linked_at,
 *   unlinked_at, created_at, updated_at, expiry_date, auto_renew,
 *   next_renewal_invoice_at. NO status, registry_lock, or cpanel_username
 *   columns — the cPanel binding is via customer_link.primary_domain.
 *
 * Status derives client-side from expiry_date relative to today.
 */

interface AdminDomainLink {
  id: string;
  domain_name: string;
  registrar: string | null;
  expiry_date: string | null;
  auto_renew: boolean | null;
  next_renewal_invoice_at: string | null;
  linked_at: string;
  unlinked_at: string | null;
}

interface AdminCustomerLinkBinding {
  cpanel_username: string;
  primary_domain: string | null;
}

type DerivedStatus = 'active' | 'expiring' | 'expired' | 'unlinked';

function deriveStatus(row: AdminDomainLink): DerivedStatus {
  if (row.unlinked_at) return 'unlinked';
  if (!row.expiry_date) return 'active';
  const expiry = new Date(row.expiry_date).getTime();
  const now = Date.now();
  const thirtyDays = 30 * 24 * 60 * 60 * 1000;
  if (expiry < now) return 'expired';
  if (expiry - now < thirtyDays) return 'expiring';
  return 'active';
}

const STATUS_TONE: Record<DerivedStatus, StatusTone> = {
  active: 'active',
  expiring: 'expiring',
  expired: 'overdue',
  unlinked: 'draft',
};

export default function CustomerDomainsTab() {
  const { customer, refresh } = useOutletContext<CustomerDetailContext>();
  const { toast } = useToast();
  const [rows, setRows] = useState<AdminDomainLink[]>([]);
  // domain_name → cpanel_username (resolved from customer_link.primary_domain)
  const [cpanelBindings, setCpanelBindings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [registerOpen, setRegisterOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      supabase
        .from('domain_link')
        .select('id, domain_name, registrar, expiry_date, auto_renew, next_renewal_invoice_at, linked_at, unlinked_at')
        .eq('customer_id', customer.customer_id)
        .is('unlinked_at', null)
        .order('linked_at', { ascending: false }),
      supabase
        .from('customer_link')
        .select('cpanel_username, primary_domain')
        .eq('customer_id', customer.customer_id)
        .is('unlinked_at', null),
    ])
      .then(([domainRes, hostingRes]) => {
        if (cancelled) return;
        if (domainRes.error) {
          setError(new Error(domainRes.error.message));
          setLoading(false);
          return;
        }
        setRows((domainRes.data ?? []) as AdminDomainLink[]);

        // Build the domain_name → cpanel_username map from customer_link.
        const bindings: Record<string, string> = {};
        for (const link of (hostingRes.data ?? []) as AdminCustomerLinkBinding[]) {
          if (link.primary_domain) bindings[link.primary_domain] = link.cpanel_username;
        }
        setCpanelBindings(bindings);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customer.customer_id]);

  return (
    <>
      <div className="mb-3 flex items-center justify-end">
        <Button variant="primary" size="sm" onClick={() => setRegisterOpen(true)}>
          <Plus className="h-4 w-4" />
          Register domain
        </Button>
      </div>

      {loading && <UniversalLoadingState variant="cards" count={2} />}
      {error && (
        <NotificationCard
          tone="danger"
          title="Could not load domains"
          body={error.message}
        />
      )}
      {!loading && !error && rows.length === 0 && (
        <Card interactive={false}>
          <UniversalEmptyState
            icon={Globe}
            title="No domains linked"
            description="Use Register domain to add a .co.za / .org.za / .net.za / .web.za for this customer. An invoice will be issued — the customer pays via PayFast and the registrar API runs after payment clears."
            action={
              <Button variant="primary" onClick={() => setRegisterOpen(true)}>
                <Plus className="h-4 w-4" />
                Register domain
              </Button>
            }
          />
        </Card>
      )}
      {!loading && !error && rows.length > 0 && (
        <div className="space-y-3">
          {rows.map((row) => (
            <DomainCard
              key={row.id}
              row={row}
              boundCpanel={cpanelBindings[row.domain_name] ?? null}
            />
          ))}
        </div>
      )}

      {registerOpen && (
        <RegisterDomainModal
          customerId={customer.customer_id}
          customerEmail={customer.email ?? ''}
          onClose={() => setRegisterOpen(false)}
          onSubmitted={(domain) => {
            setRegisterOpen(false);
            toast({
              tone: 'success',
              title: 'Domain order created',
              body: `Invoice issued for ${domain}. Customer has been emailed the PayFast link.`,
            });
            refresh();
          }}
        />
      )}
    </>
  );
}

function DomainCard({
  row,
  boundCpanel,
}: {
  row: AdminDomainLink;
  boundCpanel: string | null;
}) {
  const status = deriveStatus(row);
  const tone = STATUS_TONE[status];

  return (
    <Card interactive={false}>
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-6 py-4">
        <div className="flex items-center gap-3">
          <Globe className="h-5 w-5 text-muted-foreground" />
          <div>
            <h3 className="text-sm font-semibold text-foreground">{row.domain_name}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {row.registrar ?? 'domains.co.za'}
              {boundCpanel && (
                <> · bound to <code className="rounded bg-muted px-1">{boundCpanel}</code></>
              )}
            </p>
          </div>
        </div>
        <StatusBadge tone={tone}>{status}</StatusBadge>
      </header>
      <div className="grid grid-cols-1 gap-3 px-6 py-4 text-sm sm:grid-cols-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Expires</p>
          <p className="text-foreground">{formatDate(row.expiry_date)}</p>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Auto-renew</p>
          <p className="text-foreground">{row.auto_renew ? 'Yes' : 'No'}</p>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Next renewal invoice</p>
          <p className="text-foreground">{formatDate(row.next_renewal_invoice_at)}</p>
        </div>
      </div>
      <footer className="flex flex-wrap gap-2 border-t border-border bg-muted px-6 py-3">
        <a href={`/admin/domains/${encodeURIComponent(row.domain_name)}`}>
          <Button variant="outline" size="sm">
            <ExternalLink className="h-4 w-4" />
            Manage in /admin/domains
          </Button>
        </a>
      </footer>
    </Card>
  );
}
